// Shared Moments-bench constants and the poll-transcript → MessageStream row
// mapping. Pure module — no React, no state.
import moment from 'moment';

import { getPrettyPriceDisplay } from '../../helpers/formatting';
import { getRootSMSUrl } from '../../helpers/helpers';

export const STATUS_LABELS = {
  building: 'Building…',
  draft: 'Draft — needs review',
  published: 'Live',
  archived: 'Archived',
  failed: 'Build failed'
};

export const shortDate = iso => (iso ? moment(iso).format('MMM D') : null);

// Build costs always show cents ($1.20, not $1.2 or $1): fixed 2-digit precision.
export const formatUsd = value => getPrettyPriceDisplay(value, { precision: 2 });

// A published moment's public address lives on the primary site (the admin
// origin has no shopper page).
const PRIMARY_SITE_URL = getRootSMSUrl();

export const momentPublicUrl = slug => `${PRIMARY_SITE_URL}/moments/${slug}`;

// Poll transcript rows (AgentMessages on the moment's thread, served in the
// poll's legacy shape) → the Agent MessageStream row shape (the dock's
// AgentMessages contract): `user` / `autopilot` bubbles, `tool_call` rows with
// `tool_result` children linked by parentAgentMessage_id, and `system_event`
// narration. The poll returns the full persisted transcript, so this is a pure
// per-row mapping — a completed tool_call row expands into call + result.
export const messageToRows = row => {
  const id = `m${row.id}`;
  const at = row.createdAt || undefined;
  const metadata = row.metadata || {};
  switch (row.role) {
    case 'user':
      // The automatic retry of a build that died of a transient fault re-sends the brief the
      // transcript already carries; one line says so instead of the brief twice.
      if (metadata.autoRetryOf) {
        return [{ id, role: 'system_event', message: 'Build retried after a transient fault — brief re-sent', createdAt: at, metadata: {} }];
      }
      return [{ id, role: 'user', message: row.message || '', createdAt: at, metadata: {} }];
    case 'builder':
      if (!row.message) return [];
      return [{ id, role: 'autopilot', message: row.message, createdAt: at, metadata: { agent: row.agent || 'personal-shopper' } }];
    case 'tool_call': {
      const call = {
        id: row.toolCallId || id,
        role: 'tool_call',
        createdAt: at,
        metadata: { toolName: metadata.toolName || 'tool', toolInput: metadata.args || null, agent: row.agent || null }
      };
      if (metadata.status !== 'complete') return [call];
      return [
        call,
        {
          id: `${id}-result`,
          role: 'tool_result',
          parentAgentMessage_id: call.id,
          createdAt: at,
          metadata: { toolOutput: null, durationMs: metadata.durationMs || null }
        }
      ];
    }
    case 'system_event': {
      switch (metadata.kind) {
        case 'run.started':
        case 'run.retried':
          return [];
        case 'run.completed':
          return [
            {
              id,
              role: 'system_event',
              message: `Build complete${metadata.costUsd != null ? ` · ${formatUsd(metadata.costUsd)}` : ''}`,
              createdAt: at,
              metadata: {}
            }
          ];
        case 'run.failed': {
          // Webhook-supplied error text is data, not markdown — a crafted
          // payload must not style itself or mint a link in the transcript.
          // A code span neutralizes markdown syntax; backticks are swapped
          // and whitespace collapsed so the span can't be broken out of.
          const errorText = String(metadata.error || 'unknown error')
            .replace(/`/g, '\u2019')
            .replace(/\s+/g, ' ')
            .slice(0, 300);
          return [{ id, role: 'system_event', message: `**Build failed** — \`${errorText}\``, createdAt: at, metadata: {} }];
        }
        case 'run.cancelled':
          return [{ id, role: 'system_event', message: 'Build cancelled', createdAt: at, metadata: {} }];
        default:
          // Narration (message.created with a non-answer role).
          if (!row.message) return [];
          return [{ id, role: 'system_event', message: row.message, createdAt: at, metadata: {} }];
      }
    }
    default:
      return [];
  }
};
